import type { ReactNode } from "react";
import type {
    UseFormRegister,
    FieldErrors,
    UseFormWatch,
} from "react-hook-form";
import { Spinner } from "@common/index";
import { LEGAL_CLASS_OPTIONS, type Product } from "@models/product";
import { PRICE_UNAVAILABLE } from "@utils/formatter";
import { computeMarkup, computeMargin } from "../NewProduct/_helpers/pricing";
import { derivedAmount } from "../_helpers/derivedAmount";
import { formatStock } from "../_helpers/formatStock";
import { toNumber, type ProductFormData } from "../_helpers/productForm";
import type { LookupState } from "../_helpers/useProductFormLookups";
import { LookupLoadError } from "./LookupLoadError";

interface CategoryOption {
    id: number;
    name: string;
}

interface ProductFormProps {
    register: UseFormRegister<ProductFormData>;
    errors: FieldErrors<ProductFormData>;
    watch: UseFormWatch<ProductFormData>;
    categories: CategoryOption[];
    /** Column-1 image area: <NewProductImages /> on create, interactive image manager on edit. */
    imageSlot: ReactNode;
    /** Column-3 COA row, between Pricing and Inventory (#476). */
    coaSlot?: ReactNode;
    /**
     * The product's Stock, read-only (ruling I2): admins never type stock, it
     * comes from the vendors. Null renders an em-dash (the NEW page, which
     * has no vendors yet, or a product with no active vendor row).
     */
    stock?: number | null;
    /**
     * The product read has not landed yet (EDIT page), so Stock is unknown
     * rather than "no vendors": its caption is held back.
     */
    isStockLoading?: boolean;
    /** The read-only vendor offerings list (EDIT page only, ruling I2). */
    offeringsSlot?: ReactNode;
    /**
     * The product's Price and Cost, read-only (P1/P2, rulings 2026-10-29):
     * the API derives both from the vendor offers, so admins never type them.
     * Null renders em-dashes (the NEW page, which has no offers yet, or the
     * EDIT page before its read lands).
     */
    derivedPrice?: Pick<Product, "price" | "cost" | "hasOffers"> | null;
    /**
     * Render the Popular Rank input. Only the EDIT page sets this — createProduct
     * has no PopularRank field, so pinning is an update-only operation (E4-S4).
     */
    showPopularRank?: boolean;
    /**
     * Disable every field. The EDIT page sets this while the product is still
     * loading: the fields render before the read lands, and `reset` on load
     * would otherwise discard anything typed in that window.
     */
    disabled?: boolean;
    /**
     * Where the category list stands when it has nothing to offer: "loading"
     * renders a spinner next to the field and "failed" an inline "Couldn't
     * load … Retry" — never a silently empty dropdown. See
     * useProductFormLookups.
     */
    categoriesState?: LookupState;
    onRetryCategories?: () => void;
}

export const ProductForm = ({
    register,
    errors,
    watch,
    categories,
    imageSlot,
    coaSlot,
    stock = null,
    isStockLoading = false,
    offeringsSlot,
    derivedPrice = null,
    showPopularRank = false,
    disabled = false,
    categoriesState = "ready",
    onRetryCategories = () => {},
}: ProductFormProps) => {
    const inputClass =
        "input input-bordered input-primary w-full bg-white text-neutral";
    const selectClass =
        "select select-bordered select-primary w-full bg-white text-neutral";
    const sectionHeader =
        "bg-info/20 text-center font-bold py-2 rounded-t text-neutral";

    // Shown as text: "—" until there is a read, "Price unavailable" when no
    // vendor offer prices the product.
    const shownAmount = (field: "price" | "cost") =>
        derivedPrice === null
            ? "—"
            : (derivedAmount(derivedPrice[field], derivedPrice.hasOffers) ??
              PRICE_UNAVAILABLE);
    // An unknown or unoffered cost is NaN, so MARKUP and MARGIN stay blank
    // rather than reading a 100% margin over a cost of nothing (P4 keeps
    // the columns; with Cost == Price they read 0%).
    const isPriced =
        derivedPrice !== null && derivedPrice.hasOffers !== false;
    const cost = isPriced ? derivedPrice.cost : NaN;
    const price = isPriced ? derivedPrice.price : NaN;
    const msrp = toNumber(watch("msrp"));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            {/* ---------- Column 1: Image + IDs ---------- */}
            <div className="space-y-6">
                {imageSlot}

                <div className="border border-base-300 rounded">
                    <div className={sectionHeader}>IDs</div>
                    <div className="p-4 space-y-3">
                        <label className="flex items-center gap-3">
                            <span className="w-28 font-semibold">UPC</span>
                            <input
                                className={inputClass}
                                disabled={disabled}
                                {...register("upc")}
                            />
                        </label>
                        {errors.upc && (
                            <p className="text-red-500 text-sm">
                                {errors.upc.message}
                            </p>
                        )}
                        <label className="flex items-center gap-3">
                            <span className="w-28 font-semibold">EAN</span>
                            <input
                                className={inputClass}
                                disabled={disabled}
                                {...register("ean")}
                            />
                        </label>
                        {errors.ean && (
                            <p className="text-red-500 text-sm">
                                {errors.ean.message}
                            </p>
                        )}
                        <label className="flex items-center gap-3">
                            <span className="w-28 font-semibold">
                                Custom SKU
                            </span>
                            {/* TODO(backend): customSku — no API support yet. */}
                            <input
                                className={inputClass}
                                disabled={disabled}
                                {...register("customSku")}
                            />
                        </label>
                        <label className="flex items-center gap-3">
                            <span className="w-28 font-semibold">
                                Manufact. SKU
                            </span>
                            <input
                                className={inputClass}
                                disabled={disabled}
                                {...register("manufacturerSku")}
                            />
                        </label>
                        {errors.manufacturerSku && (
                            <p className="text-red-500 text-sm">
                                {errors.manufacturerSku.message}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------- Column 2: Name / Category / Legal / Description ---------- */}
            <div className="space-y-4">
                <div className="border border-base-300 rounded">
                    <div className={sectionHeader}>Name</div>
                    <div className="p-4">
                        <input
                            className={inputClass}
                            placeholder="Name"
                            disabled={disabled}
                            {...register("name")}
                        />
                        {errors.name && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.name.message}
                            </p>
                        )}
                        {/* Not in the mockup, but both product validators
                            require it — without it no product could be
                            created (#430). */}
                        <input
                            className={`${inputClass} mt-3`}
                            placeholder="Brand"
                            aria-label="Brand"
                            disabled={disabled}
                            {...register("brand")}
                        />
                        {errors.brand && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.brand.message}
                            </p>
                        )}
                        {/* TODO(design): + Variant button — blocked on E3-S6 design
                            clarification. Do not build until Design responds. */}
                    </div>
                </div>

                <div>
                    <select
                        className={selectClass}
                        defaultValue=""
                        aria-label="Category"
                        disabled={disabled}
                        {...register("categoryId")}
                    >
                        <option value="" disabled>
                            Category
                        </option>
                        {categories.map((category) => (
                            <option
                                key={category.id}
                                value={String(category.id)}
                            >
                                {category.name}
                            </option>
                        ))}
                    </select>
                    {categoriesState === "loading" && (
                        <Spinner className="mt-1" />
                    )}
                    {categoriesState === "failed" && (
                        <LookupLoadError
                            message="Couldn't load categories."
                            onRetry={onRetryCategories}
                        />
                    )}
                    {errors.categoryId && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.categoryId.message}
                        </p>
                    )}
                </div>

                {/* A product carries any number of legal classes, and the
                    update replaces the whole list — so every class is shown
                    and sent, and unticking them all is a product with none
                    (#431). Five boxes registered under one name: react-hook-
                    form reads them as the array of ticked values. */}
                <fieldset className="border border-base-300 rounded p-4">
                    <legend className="font-semibold px-1">
                        Legal Classes
                    </legend>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                        {LEGAL_CLASS_OPTIONS.map((option) => (
                            <label
                                key={option.value}
                                className="flex cursor-pointer items-center gap-2"
                            >
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary"
                                    value={option.value}
                                    disabled={disabled}
                                    {...register("legalClasses")}
                                />
                                {option.label}
                            </label>
                        ))}
                    </div>
                </fieldset>

                <textarea
                    className="textarea textarea-bordered textarea-primary w-full bg-white text-neutral"
                    rows={6}
                    placeholder="Description"
                    disabled={disabled}
                    {...register("description")}
                />
                {errors.description && (
                    <p className="text-red-500 text-sm">
                        {errors.description.message}
                    </p>
                )}

                {showPopularRank && (
                    <label className="block">
                        <span className="font-semibold block mb-1">
                            Popular Rank
                        </span>
                        <input
                            type="number"
                            min={1}
                            className={inputClass}
                            placeholder="Leave blank to unpin"
                            aria-label="Popular Rank"
                            disabled={disabled}
                            {...register("popularRank")}
                        />
                        {errors.popularRank && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.popularRank.message}
                            </p>
                        )}
                        <span className="text-xs text-gray-500 mt-1 block">
                            Lower numbers pin higher in popular lists. Leave
                            blank to remove from popular.
                        </span>
                    </label>
                )}
            </div>

            {/* ---------- Column 3: Pricing + Inventory ---------- */}
            <div className="space-y-6">
                <div className="border border-base-300 rounded">
                    <div className={sectionHeader}>Pricing</div>
                    <div className="p-4 space-y-3">
                        <div className="grid grid-cols-[5rem_1fr_4rem_4rem] gap-2 text-xs text-gray-500 items-end">
                            <span />
                            <span className="text-left">PRICE</span>
                            <span>MARKUP</span>
                            <span>MARGIN</span>
                        </div>
                        <div className="grid grid-cols-[5rem_1fr_4rem_4rem] gap-2 items-center">
                            <span className="font-semibold">Default</span>
                            {/* Text, not an input (P1): derived by the API
                                from the first live vendor offer, in stock
                                first, then cheapest (Q1). */}
                            <span
                                className="text-neutral"
                                data-testid="default-price"
                            >
                                {shownAmount("price")}
                            </span>
                            <span
                                className="text-sm text-center"
                                data-testid="default-markup"
                            >
                                {computeMarkup(cost, price)}
                            </span>
                            <span
                                className="text-sm text-center"
                                data-testid="default-margin"
                            >
                                {computeMargin(cost, price)}
                            </span>
                        </div>
                        <div className="grid grid-cols-[5rem_1fr_4rem_4rem] gap-2 items-center">
                            <span className="font-semibold">MSRP</span>
                            <label className="input input-bordered input-primary flex items-center gap-1 bg-white text-neutral">
                                $
                                <input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    aria-label="MSRP Price"
                                    className="grow"
                                    disabled={disabled}
                                    {...register("msrp")}
                                />
                            </label>
                            <span
                                className="text-sm text-center"
                                data-testid="msrp-markup"
                            >
                                {computeMarkup(cost, msrp)}
                            </span>
                            <span
                                className="text-sm text-center"
                                data-testid="msrp-margin"
                            >
                                {computeMargin(cost, msrp)}
                            </span>
                        </div>
                        <label className="flex items-center gap-2 pt-2">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary"
                                disabled={disabled}
                                {...register("discountAllowed")}
                            />
                            <span>Discounts allowed</span>
                        </label>
                    </div>
                </div>

                {coaSlot}

                <div className="border border-base-300 rounded">
                    <div className={sectionHeader}>Inventory</div>
                    <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            {/* Text, not an input (P1): the Cost is the
                                derived Price. */}
                            <dl>
                                <dt className="font-semibold mb-1">
                                    Default Cost
                                </dt>
                                <dd className="py-2">
                                    {shownAmount("cost")}
                                </dd>
                            </dl>
                            {/* Text, not an input (ruling I2): admins never
                                type stock. A term and its value, not a live
                                region: the value is read-only, not a status. */}
                            <div>
                                <dl>
                                    <dt className="font-semibold mb-1">
                                        Stock
                                    </dt>
                                    <dd className="py-2">
                                        {formatStock(stock)}
                                    </dd>
                                </dl>
                                {/* Not while the product is loading, so an
                                    unknown stock doesn't read as "no
                                    vendors". */}
                                {!isStockLoading && (
                                    <span className="text-xs text-gray-500 block">
                                        Total from active vendors.
                                    </span>
                                )}
                            </div>
                        </div>
                        {offeringsSlot}
                    </div>
                </div>
            </div>
        </div>
    );
};
